import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { financeiroApi, igrejasApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Plus,
  DollarSign,
  TrendingUp,
  TrendingDown,
  Trash2,
  Loader2,
  ArrowUpRight,
  ArrowDownRight,
  PieChart,
  FileText,
  Download,
  Paperclip,
  FileBarChart,
  Building2,
  BarChart2,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  PieChart as RechartsPie,
  Pie,
  Cell
} from 'recharts';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { toast } from 'sonner';

const tipoLancamentoOptions = [
  { value: 'dizimo', label: 'Dízimo', color: 'sky' },
  { value: 'oferta_geral', label: 'Oferta Geral', color: 'emerald' },
  { value: 'oferta_especifica', label: 'Oferta Específica', color: 'violet' },
  { value: 'despesas', label: 'Despesas', color: 'amber' },
];

const categoriasDespesas = [
  'Aluguel',
  'Energia',
  'Água',
  'Internet',
  'Manutenção',
  'Material de Limpeza',
  'Material de Escritório',
  'Eventos',
  'Missões',
  'Salários',
  'Outros',
];

const COLORS = ['#0ea5e9', '#10b981', '#8b5cf6', '#f59e0b', '#ef4444', '#6366f1'];

const reportTipoOptions = [
  { value: 'geral', label: 'Geral' },
  { value: 'entradas', label: 'Entradas' },
  { value: 'despesas', label: 'Despesas' },
  { value: 'dizimo', label: 'Dízimo' },
  { value: 'oferta_geral', label: 'Oferta Geral' },
  { value: 'oferta_especifica', label: 'Oferta Específica' },
];

const TIPO_LABELS = {
  dizimo: 'Dízimo',
  oferta_geral: 'Oferta Geral',
  oferta_especifica: 'Oferta Específica',
  despesa: 'Despesa',
  despesas: 'Despesas',
};

const TIPO_CHART_COLORS = {
  dizimo: '#0ea5e9',
  oferta_geral: '#10b981',
  oferta_especifica: '#8b5cf6',
  despesa: '#f59e0b',
  despesas: '#f59e0b',
};

export const Financeiro = () => {
  const { user } = useAuth();
  const [resumo, setResumo] = useState({});
  const [lancamentos, setLancamentos] = useState([]);
  const [graficoData, setGraficoData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, lancamento: null });
  const [comprovanteModal, setComprovanteModal] = useState({ open: false, url: null });
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('resumo');
  const [igrejas, setIgrejas] = useState([]);
  const [reportScope, setReportScope] = useState('ministerio');
  const [reportIgrejaId, setReportIgrejaId] = useState('');
  const [reportType, setReportType] = useState('geral');
  const [reportPeriod, setReportPeriod] = useState('mes_atual');
  const [reportDataInicio, setReportDataInicio] = useState('');
  const [reportDataFim, setReportDataFim] = useState('');
  const [reportResumo, setReportResumo] = useState(null);
  const [reportResumoLoading, setReportResumoLoading] = useState(false);
  const [reportLancamentos, setReportLancamentos] = useState([]);
  const [reportPorIgreja, setReportPorIgreja] = useState(null);
  const reportFilterChangedAt = useRef(0);
  const reportPdfRef = useRef(null);
  const [reportPdfLoading, setReportPdfLoading] = useState(false);
  const [formData, setFormData] = useState({
    tipo: 'dizimo',
    valor: '',
    data: format(new Date(), 'yyyy-MM-dd'),
    descricao: '',
    categoria: '',
    comprovante: null,
  });

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    const fetchIgrejas = async () => {
      try {
        const res = await igrejasApi.list();
        setIgrejas(res.data || []);
      } catch (err) {
        console.error('Error fetching igrejas:', err);
      }
    };
    if (activeTab === 'relatorios') fetchIgrejas();
  }, [activeTab]);

  const getMesAtualRange = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();
    const dataInicio = format(new Date(year, month, 1), 'yyyy-MM-dd');
    const dataFim = format(new Date(year, month + 1, 0), 'yyyy-MM-dd');
    return { dataInicio, dataFim, mes: month + 1, ano: year };
  };

  const reportChartData = useMemo(() => {
    if (!reportLancamentos?.length) return [];
    const tiposOrderGeral = ['dizimo', 'oferta_geral', 'oferta_especifica', 'despesas'];
    const tiposOrderEntradas = ['dizimo', 'oferta_geral', 'oferta_especifica'];
    const singleReportTypes = ['dizimo', 'oferta_geral', 'oferta_especifica'];
    if (reportType === 'despesas') {
      const byCategoria = {};
      reportLancamentos.forEach((l) => {
        if (l.tipo !== 'despesa' && l.tipo !== 'despesas') return;
        const cat = l.categoria?.trim() || 'Outros';
        byCategoria[cat] = (byCategoria[cat] || 0) + (parseFloat(l.valor) || 0);
      });
      return Object.entries(byCategoria)
        .map(([categoria, valor]) => ({ categoria, valor }))
        .filter((row) => row.valor > 0)
        .sort((a, b) => b.valor - a.valor);
    }
    if (singleReportTypes.includes(reportType)) return [];
    if (reportType === 'geral') {
      const byTipo = {};
      tiposOrderGeral.forEach((t) => { byTipo[t] = 0; });
      reportLancamentos.forEach((l) => {
        const t = l.tipo === 'despesa' ? 'despesas' : (l.tipo in TIPO_LABELS ? l.tipo : 'despesas');
        byTipo[t] = (byTipo[t] || 0) + (parseFloat(l.valor) || 0);
      });
      return tiposOrderGeral
        .map((tipoKey) => ({
          tipoKey,
          tipo: TIPO_LABELS[tipoKey],
          valor: byTipo[tipoKey] || 0,
        }))
        .filter((row) => row.valor > 0);
    }
    if (reportType === 'entradas') {
      const byTipo = {};
      tiposOrderEntradas.forEach((t) => { byTipo[t] = 0; });
      reportLancamentos.forEach((l) => {
        const t = l.tipo in TIPO_LABELS ? l.tipo : null;
        if (t && tiposOrderEntradas.includes(t)) byTipo[t] = (byTipo[t] || 0) + (parseFloat(l.valor) || 0);
      });
      return tiposOrderEntradas
        .map((tipoKey) => ({
          tipoKey,
          tipo: TIPO_LABELS[tipoKey],
          valor: byTipo[tipoKey] || 0,
        }))
        .filter((row) => row.valor > 0);
    }
    return [];
  }, [reportLancamentos, reportType]);

  const reportChartDataMinisterio = useMemo(() => {
    if (!reportPorIgreja?.igrejas?.length) return [];
    const getValor = (ig) => {
      switch (reportType) {
        case 'geral': return ig.saldo ?? 0;
        case 'entradas': return ig.total_entradas ?? 0;
        case 'despesas': return ig.total_despesas ?? 0;
        case 'dizimo': return ig.total_dizimos ?? 0;
        case 'oferta_geral':
        case 'oferta_especifica': return ig.total_ofertas ?? 0;
        default: return ig.saldo ?? 0;
      }
    };
    return reportPorIgreja.igrejas.map((item) => ({
      nome: item.igreja?.nome || 'Igreja',
      valor: getValor(item),
    }));
  }, [reportPorIgreja, reportType]);

  const reportDetailByIgreja = useMemo(() => {
    const buildLinhasFromLancamentos = (lancamentos) => {
      if (!lancamentos?.length) return [];
      const byTipo = {};
      const byCategoria = {};
      lancamentos.forEach((l) => {
        const t = l.tipo === 'despesa' ? 'despesas' : l.tipo;
        byTipo[t] = (byTipo[t] || 0) + (parseFloat(l.valor) || 0);
        if (t === 'despesas' || t === 'despesa') {
          const cat = l.categoria?.trim() || 'Outros';
          byCategoria[cat] = (byCategoria[cat] || 0) + (parseFloat(l.valor) || 0);
        }
      });
      if (reportType === 'geral') {
        const entradas = (byTipo.dizimo || 0) + (byTipo.oferta_geral || 0) + (byTipo.oferta_especifica || 0);
        const despesas = byTipo.despesas || 0;
        return [
          { label: 'Dízimos', valor: byTipo.dizimo || 0 },
          { label: 'Ofertas', valor: (byTipo.oferta_geral || 0) + (byTipo.oferta_especifica || 0) },
          { label: 'Despesas', valor: despesas },
          { label: 'Total entradas', valor: entradas },
          { label: 'Saldo', valor: entradas - despesas },
        ];
      }
      if (reportType === 'entradas') {
        return [
          { label: TIPO_LABELS.dizimo, valor: byTipo.dizimo || 0 },
          { label: TIPO_LABELS.oferta_geral, valor: byTipo.oferta_geral || 0 },
          { label: TIPO_LABELS.oferta_especifica, valor: byTipo.oferta_especifica || 0 },
          { label: 'Total entradas', valor: (byTipo.dizimo || 0) + (byTipo.oferta_geral || 0) + (byTipo.oferta_especifica || 0) },
        ];
      }
      if (reportType === 'despesas') {
        const linhas = Object.entries(byCategoria).map(([cat, valor]) => ({ label: cat, valor }));
        linhas.sort((a, b) => b.valor - a.valor);
        linhas.push({ label: 'Total despesas', valor: byTipo.despesas || 0 });
        return linhas;
      }
      if (reportType === 'dizimo') return [{ label: TIPO_LABELS.dizimo, valor: byTipo.dizimo || 0 }];
      if (reportType === 'oferta_geral') return [{ label: TIPO_LABELS.oferta_geral, valor: byTipo.oferta_geral || 0 }];
      if (reportType === 'oferta_especifica') return [{ label: TIPO_LABELS.oferta_especifica, valor: byTipo.oferta_especifica || 0 }];
      return [];
    };
    if (reportScope === 'ministerio' && reportPorIgreja?.igrejas?.length) {
      return reportPorIgreja.igrejas.map((item) => {
        const nome = item.igreja?.nome || 'Igreja';
        let linhas = [];
        if (reportType === 'geral') {
          linhas = [
            { label: 'Dízimos', valor: item.total_dizimos ?? 0 },
            { label: 'Ofertas', valor: item.total_ofertas ?? 0 },
            { label: 'Despesas', valor: item.total_despesas ?? 0 },
            { label: 'Total entradas', valor: item.total_entradas ?? 0 },
            { label: 'Saldo', valor: item.saldo ?? 0 },
          ];
        } else if (reportType === 'entradas') {
          const fromLanc = (item.lancamentos || []).reduce((acc, l) => {
            const t = l.tipo === 'despesa' ? 'despesas' : l.tipo;
            if (t && t !== 'despesas') acc[t] = (acc[t] || 0) + (parseFloat(l.valor) || 0);
            return acc;
          }, {});
          linhas = [
            { label: TIPO_LABELS.dizimo, valor: fromLanc.dizimo ?? item.total_dizimos ?? 0 },
            { label: TIPO_LABELS.oferta_geral, valor: fromLanc.oferta_geral ?? 0 },
            { label: TIPO_LABELS.oferta_especifica, valor: fromLanc.oferta_especifica ?? 0 },
            { label: 'Total entradas', valor: item.total_entradas ?? 0 },
          ];
        } else if (reportType === 'despesas') {
          const byCat = {};
          (item.lancamentos || []).forEach((l) => {
            if (l.tipo !== 'despesa' && l.tipo !== 'despesas') return;
            const cat = l.categoria?.trim() || 'Outros';
            byCat[cat] = (byCat[cat] || 0) + (parseFloat(l.valor) || 0);
          });
          linhas = Object.entries(byCat).map(([cat, valor]) => ({ label: cat, valor })).sort((a, b) => b.valor - a.valor);
          linhas.push({ label: 'Total despesas', valor: item.total_despesas ?? 0 });
        } else if (reportType === 'dizimo') {
          linhas = [{ label: TIPO_LABELS.dizimo, valor: item.total_dizimos ?? 0 }];
        } else if (reportType === 'oferta_geral' || reportType === 'oferta_especifica') {
          linhas = [{ label: reportType === 'oferta_geral' ? TIPO_LABELS.oferta_geral : TIPO_LABELS.oferta_especifica, valor: item.total_ofertas ?? 0 }];
        }
        return { nome, linhas };
      });
    }
    if (reportScope === 'igreja' && reportLancamentos?.length) {
      const nome = igrejas.find((i) => i.id === reportIgrejaId)?.nome || 'Igreja';
      const linhas = buildLinhasFromLancamentos(reportLancamentos);
      return linhas.length ? [{ nome, linhas }] : [];
    }
    return [];
  }, [reportScope, reportPorIgreja, reportLancamentos, reportType, reportIgrejaId, igrejas]);

  const handleDownloadReportPdf = async () => {
    if (!reportPdfRef.current) return;
    setReportPdfLoading(true);
    try {
      const [{ default: html2canvas }, { jsPDF }] = await Promise.all([
        import('html2canvas'),
        import('jspdf'),
      ]);
      const canvas = await html2canvas(reportPdfRef.current, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
      });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const margin = 10;
      const pageWidth = pdfWidth - 2 * margin;
      const pageHeight = pdfHeight - 2 * margin;
      const imgWidth = pageWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      pdf.addImage(imgData, 'PNG', margin, margin, imgWidth, imgHeight);
      let position = pageHeight;
      while (position < imgHeight) {
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', margin, margin - position, imgWidth, imgHeight);
        position += pageHeight;
      }
      const fileName = `relatorio-financeiro-${format(new Date(), 'yyyy-MM-dd-HHmm')}.pdf`;
      pdf.save(fileName);
      toast.success('Relatório baixado com sucesso');
    } catch (err) {
      console.error('Erro ao gerar PDF:', err);
      toast.error('Erro ao baixar relatório em PDF');
    } finally {
      setReportPdfLoading(false);
    }
  };

  const fetchReportResumo = async () => {
    if (Date.now() - reportFilterChangedAt.current < 400) return;
    if (reportScope === 'igreja' && !reportIgrejaId) {
      toast.error('Selecione uma igreja');
      return;
    }
    if (reportPeriod === 'personalizado') {
      if (!reportDataInicio || !reportDataFim) {
        toast.error('Informe a data de início e fim');
        return;
      }
      if (reportDataInicio > reportDataFim) {
        toast.error('Data de início deve ser anterior à data de fim');
        return;
      }
    }

    setReportResumoLoading(true);
    setReportLancamentos([]);
    setReportPorIgreja(null);
    try {
      const params = {};
      if (reportScope === 'igreja' && reportIgrejaId) {
        params.igreja_id = reportIgrejaId;
      }
      if (reportPeriod === 'mes_atual') {
        const { dataInicio, dataFim, mes, ano } = getMesAtualRange();
        params.data_inicio = dataInicio;
        params.data_fim = dataFim;
        params.mes = mes;
        params.ano = ano;
      } else {
        params.data_inicio = reportDataInicio;
        params.data_fim = reportDataFim;
      }
      if (reportType !== 'geral') params.tipo = reportType;
      const res = await financeiroApi.getResumo(params);
      setReportResumo(res.data || {});

      if (reportScope === 'igreja' && reportIgrejaId) {
        const lancParams = {
          igreja_id: reportIgrejaId,
          data_inicio: params.data_inicio,
          data_fim: params.data_fim,
        };
        if (reportType !== 'geral') lancParams.tipo = reportType;
        const lancRes = await financeiroApi.listLancamentos(lancParams);
        setReportLancamentos(lancRes.data || []);
      } else if (reportScope === 'ministerio' && user?.igreja_id) {
        const porIgrejaParams = {
          sede_id: user.igreja_id,
          data_inicio: params.data_inicio,
          data_fim: params.data_fim,
        };
        if (reportType !== 'geral') porIgrejaParams.tipo = reportType;
        const porIgrejaRes = await financeiroApi.getLancamentosPorIgreja(porIgrejaParams);
        setReportPorIgreja(porIgrejaRes.data || null);
      }
    } catch (err) {
      console.error('Error fetching report resumo:', err);
      toast.error('Erro ao gerar relatório');
      setReportResumo(null);
      setReportLancamentos([]);
      setReportPorIgreja(null);
    } finally {
      setReportResumoLoading(false);
    }
  };

  const fetchData = async () => {
    try {
      const [resumoRes, lancamentosRes, graficoRes] = await Promise.all([
        financeiroApi.getResumo({ igreja_id: user?.igreja_id }),
        financeiroApi.listLancamentos({ igreja_id: user?.igreja_id }),
        financeiroApi.getGraficoMensal({ igreja_id: user?.igreja_id }),
      ]);
      setResumo(resumoRes.data || {});
      setLancamentos(lancamentosRes.data || []);
      setGraficoData(graficoRes.data || []);
    } catch (error) {
      console.error('Error fetching financial data:', error);
      toast.error('Erro ao carregar dados financeiros');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value || 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.valor || parseFloat(formData.valor) <= 0) {
      toast.error('Informe um valor válido');
      return;
    }

    setSubmitting(true);
    try {
      const isDespesa = formData.tipo === 'despesas';
      const payload = {
        tipo: formData.tipo,
        valor: parseFloat(formData.valor),
        data: formData.data,
        descricao: formData.descricao || '',
        categoria: formData.categoria || '',
        igreja_id: user?.igreja_id,
      };

      if (isDespesa && formData.comprovante) {
        const formDataToSend = new FormData();
        Object.entries(payload).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            formDataToSend.append(key, value);
          }
        });
        formDataToSend.append('comprovante', formData.comprovante);
        await financeiroApi.createLancamento(formDataToSend);
      } else {
        await financeiroApi.createLancamento(payload);
      }

      toast.success('Lançamento registrado com sucesso!');
      setDialogOpen(false);
      setFormData({
        tipo: 'dizimo',
        valor: '',
        data: format(new Date(), 'yyyy-MM-dd'),
        descricao: '',
        categoria: '',
        comprovante: null,
      });
      fetchData();
    } catch (error) {
      toast.error('Erro ao registrar lançamento');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    try {
      await financeiroApi.deleteLancamento(deleteDialog.lancamento.id);
      toast.success('Lançamento excluído com sucesso');
      setDeleteDialog({ open: false, lancamento: null });
      fetchData();
    } catch (error) {
      toast.error('Erro ao excluir lançamento');
    }
  };

  const getTipoConfig = (tipo) => {
    const value = tipo === 'despesa' ? 'despesas' : tipo;
    return tipoLancamentoOptions.find((t) => t.value === value) || {};
  };

  const despesasPorCategoria = lancamentos
    .filter((l) => l.tipo === 'despesa' || l.tipo === 'despesas')
    .reduce((acc, l) => {
      const cat = l.categoria || 'Outros';
      acc[cat] = (acc[cat] || 0) + l.valor;
      return acc;
    }, {});

  const pieData = Object.entries(despesasPorCategoria).map(([name, value]) => ({
    name,
    value,
  }));

  return (
    <div className="space-y-6 animate-fade-in" data-testid="financeiro-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="page-title">Financeiro</h1>
          <p className="page-subtitle">Gerencie as finanças da sua igreja</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="btn-primary" data-testid="add-lancamento-btn">
              <Plus className="mr-2 h-4 w-4" />
              Novo Lançamento
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Novo Lançamento</DialogTitle>
              <DialogDescription>Registre uma entrada ou saída</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="tipo">Tipo *</Label>
                <Select
                  value={formData.tipo}
                  onValueChange={(v) => setFormData({ ...formData, tipo: v })}
                >
                  <SelectTrigger className="input-field mt-1" data-testid="select-tipo-lancamento">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {tipoLancamentoOptions.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="valor">Valor *</Label>
                <Input
                  id="valor"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.valor}
                  onChange={(e) => setFormData({ ...formData, valor: e.target.value })}
                  required
                  className="input-field mt-1"
                  placeholder="0,00"
                  data-testid="input-valor"
                />
              </div>

              <div>
                <Label htmlFor="data">Data *</Label>
                <Input
                  id="data"
                  type="date"
                  value={formData.data}
                  onChange={(e) => setFormData({ ...formData, data: e.target.value })}
                  required
                  className="input-field mt-1"
                  data-testid="input-data-lancamento"
                />
              </div>

              {formData.tipo === 'despesas' && (
                <div>
                  <Label htmlFor="categoria">Categoria</Label>
                  <Select
                    value={formData.categoria}
                    onValueChange={(v) => setFormData({ ...formData, categoria: v })}
                  >
                    <SelectTrigger className="input-field mt-1" data-testid="select-categoria">
                      <SelectValue placeholder="Selecione" />
                    </SelectTrigger>
                    <SelectContent>
                      {categoriasDespesas.map((cat) => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {formData.tipo === 'despesas' && (
                <div>
                  <Label htmlFor="comprovante">Comprovante</Label>
                  <div className="mt-1 flex items-center gap-3">
                    <Input
                      id="comprovante"
                      type="file"
                      accept="image/*,.pdf,application/pdf"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        setFormData((prev) => ({ ...prev, comprovante: file || null }));
                      }}
                      className="input-field cursor-pointer file:mr-2 file:py-2 file:px-3 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-sky-50 file:text-sky-700 hover:file:bg-sky-100"
                      data-testid="input-comprovante"
                    />
                    {formData.comprovante && (
                      <span className="flex items-center gap-1 text-sm text-slate-600 truncate max-w-[140px]" title={formData.comprovante.name}>
                        <FileText className="h-4 w-4 flex-shrink-0 text-sky-600" />
                        {formData.comprovante.name}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 mt-1">Anexe imagem ou PDF (opcional)</p>
                </div>
              )}

              <div>
                <Label htmlFor="descricao">Descrição</Label>
                <Input
                  id="descricao"
                  value={formData.descricao}
                  onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                  className="input-field mt-1"
                  placeholder="Descrição do lançamento"
                  data-testid="input-descricao"
                />
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button type="submit" className="btn-primary" disabled={submitting} data-testid="submit-lancamento">
                  {submitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Salvando...
                    </>
                  ) : (
                    'Registrar'
                  )}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="stat-card bg-gradient-to-br from-sky-50 to-white" data-testid="card-total-dizimos">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-sky-100">
                <DollarSign className="w-5 h-5 text-sky-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Dízimos</p>
                <p className="text-xl font-bold text-slate-900 font-heading">
                  {formatCurrency(resumo.total_dizimos)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card bg-gradient-to-br from-emerald-50 to-white" data-testid="card-total-ofertas">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-emerald-100">
                <TrendingUp className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Ofertas</p>
                <p className="text-xl font-bold text-slate-900 font-heading">
                  {formatCurrency(resumo.total_ofertas)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card bg-gradient-to-br from-amber-50 to-white" data-testid="card-total-despesas">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-100">
                <TrendingDown className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Despesas</p>
                <p className="text-xl font-bold text-slate-900 font-heading">
                  {formatCurrency(resumo.total_despesas)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className={`stat-card ${resumo.saldo >= 0 ? 'bg-gradient-to-br from-emerald-50 to-white' : 'bg-gradient-to-br from-red-50 to-white'}`} data-testid="card-saldo">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${resumo.saldo >= 0 ? 'bg-emerald-100' : 'bg-red-100'}`}>
                {resumo.saldo >= 0 ? (
                  <ArrowUpRight className="w-5 h-5 text-emerald-600" />
                ) : (
                  <ArrowDownRight className="w-5 h-5 text-red-600" />
                )}
              </div>
              <div>
                <p className="text-xs text-slate-500">Saldo</p>
                <p className={`text-xl font-bold font-heading ${resumo.saldo >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  {formatCurrency(resumo.saldo)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="resumo">Resumo</TabsTrigger>
          <TabsTrigger value="lancamentos">Lançamentos</TabsTrigger>
          <TabsTrigger value="graficos">Gráficos</TabsTrigger>
          <TabsTrigger value="relatorios">Relatórios</TabsTrigger>
        </TabsList>

        {/* Resumo Tab */}
        <TabsContent value="resumo">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="stat-card" data-testid="chart-mensal">
              <CardHeader>
                <CardTitle className="card-title">Movimento Mensal</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={graficoData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="nome_mes" tick={{ fill: '#64748b', fontSize: 12 }} />
                      <YAxis tick={{ fill: '#64748b', fontSize: 12 }} />
                      <Tooltip
                        formatter={(value) => formatCurrency(value)}
                        contentStyle={{
                          backgroundColor: 'white',
                          border: '1px solid #e2e8f0',
                          borderRadius: '8px',
                        }}
                      />
                      <Legend />
                      <Bar dataKey="entradas" name="Entradas" fill="#0ea5e9" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="saidas" name="Saídas" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <Card className="stat-card" data-testid="chart-despesas-categoria">
              <CardHeader>
                <CardTitle className="card-title">Despesas por Categoria</CardTitle>
              </CardHeader>
              <CardContent>
                {pieData.length > 0 ? (
                  <div className="h-72">
                    <ResponsiveContainer width="100%" height="100%">
                      <RechartsPie>
                        <Pie
                          data={pieData}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                          outerRadius={80}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {pieData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value) => formatCurrency(value)} />
                      </RechartsPie>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="h-72 flex items-center justify-center text-slate-400">
                    <div className="text-center">
                      <PieChart className="w-12 h-12 mx-auto mb-2" />
                      <p>Nenhuma despesa registrada</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Relatórios Tab */}
        <TabsContent value="relatorios" className="space-y-6">
          <Card className="stat-card" data-testid="relatorios-filtros">
            <CardHeader>
              <CardTitle className="card-title flex items-center gap-2">
                <FileBarChart className="h-5 w-5 text-sky-600" />
                Filtros do relatório
              </CardTitle>
              <CardDescription>Escolha o escopo, tipo e período para gerar o relatório</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Escopo: Todo o ministério ou Igreja */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-slate-500" />
                  Escopo
                </Label>
                <div className="flex flex-col sm:flex-row gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="reportScope"
                      checked={reportScope === 'ministerio'}
                      onChange={() => { reportFilterChangedAt.current = Date.now(); setReportScope('ministerio'); setReportIgrejaId(''); setReportResumo(null); setReportLancamentos([]); setReportPorIgreja(null); }}
                      className="rounded-full border-slate-300 text-sky-600 focus:ring-sky-500"
                    />
                    <span className="text-sm font-medium text-slate-700">Todo o ministério</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="reportScope"
                      checked={reportScope === 'igreja'}
                      onChange={() => { reportFilterChangedAt.current = Date.now(); setReportScope('igreja'); setReportResumo(null); setReportLancamentos([]); setReportPorIgreja(null); }}
                      className="rounded-full border-slate-300 text-sky-600 focus:ring-sky-500"
                    />
                    <span className="text-sm font-medium text-slate-700">Sede / Congregação / Subcongregação</span>
                  </label>
                </div>
                {reportScope === 'igreja' && (
                  <Select value={reportIgrejaId} onValueChange={(v) => { reportFilterChangedAt.current = Date.now(); setReportIgrejaId(v); setReportResumo(null); setReportLancamentos([]); setReportPorIgreja(null); }}>
                    <SelectTrigger className="input-field mt-2 max-w-md" data-testid="select-igreja-relatorio">
                      <SelectValue placeholder="Selecione a igreja" />
                    </SelectTrigger>
                    <SelectContent>
                      {igrejas.map((ig) => (
                        <SelectItem key={ig.id} value={ig.id}>{ig.nome}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>

              {/* Tipo de relatório */}
              <div className="space-y-2">
                <Label htmlFor="report-tipo">Tipo de relatório</Label>
                <Select value={reportType} onValueChange={(v) => { reportFilterChangedAt.current = Date.now(); setReportType(v); setReportResumo(null); setReportLancamentos([]); setReportPorIgreja(null); }}>
                  <SelectTrigger id="report-tipo" className="input-field max-w-md" data-testid="select-tipo-relatorio">
                    <SelectValue placeholder="Selecione o tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    {reportTipoOptions.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Período */}
              <div className="space-y-2">
                <Label>Período</Label>
                <Tabs value={reportPeriod} onValueChange={(v) => { reportFilterChangedAt.current = Date.now(); setReportPeriod(v); setReportResumo(null); setReportLancamentos([]); setReportPorIgreja(null); }} className="w-full">
                  <TabsList className="grid w-full grid-cols-2 bg-slate-100 max-w-md">
                    <TabsTrigger value="mes_atual">Mês atual</TabsTrigger>
                    <TabsTrigger value="personalizado">Personalizado</TabsTrigger>
                  </TabsList>
                </Tabs>
                {reportPeriod === 'personalizado' && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4 max-w-lg">
                    <div>
                      <Label htmlFor="report-data-inicio">Data início</Label>
                      <Input
                        id="report-data-inicio"
                        type="date"
                        value={reportDataInicio}
                        onChange={(e) => { reportFilterChangedAt.current = Date.now(); setReportDataInicio(e.target.value); setReportResumo(null); setReportLancamentos([]); setReportPorIgreja(null); }}
                        className="input-field mt-1"
                        data-testid="report-data-inicio"
                      />
                    </div>
                    <div>
                      <Label htmlFor="report-data-fim">Data fim</Label>
                      <Input
                        id="report-data-fim"
                        type="date"
                        value={reportDataFim}
                        onChange={(e) => { reportFilterChangedAt.current = Date.now(); setReportDataFim(e.target.value); setReportResumo(null); setReportLancamentos([]); setReportPorIgreja(null); }}
                        className="input-field mt-1"
                        data-testid="report-data-fim"
                      />
                    </div>
                  </div>
                )}
              </div>

              <Button
                type="button"
                className="btn-primary"
                onClick={fetchReportResumo}
                disabled={reportResumoLoading || (reportPeriod === 'personalizado' && (!reportDataInicio || !reportDataFim))}
                data-testid="btn-gerar-relatorio"
              >
                {reportResumoLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Gerando...
                  </>
                ) : (
                  <>
                    <FileBarChart className="mr-2 h-4 w-4" />
                    Gerar relatório
                  </>
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Conteúdo do relatório */}
          <div className="space-y-6">
            {reportResumo !== null && (
              <>
                <div ref={reportPdfRef} className="space-y-6 bg-white p-4 rounded-lg">
                {reportType === 'geral' && (
                  <Card className="stat-card">
                    <CardHeader>
                      <CardTitle>Relatório geral</CardTitle>
                      <CardDescription>
                        {reportScope === 'ministerio' ? 'Todo o ministério' : igrejas.find((i) => i.id === reportIgrejaId)?.nome || 'Igreja'} —{' '}
                        {reportPeriod === 'mes_atual' ? 'Mês atual' : `${reportDataInicio} a ${reportDataFim}`}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <div className="rounded-lg border border-slate-200 bg-emerald-50 p-4 flex items-center gap-3">
                          <TrendingUp className="h-8 w-8 text-emerald-600 flex-shrink-0" />
                          <div>
                            <p className="text-xs text-slate-600">Entradas (dízimos + ofertas)</p>
                            <p className="text-lg font-bold text-slate-900">
                              {formatCurrency((reportResumo.total_dizimos || 0) + (reportResumo.total_ofertas || 0))}
                            </p>
                          </div>
                        </div>
                        <div className="rounded-lg border border-slate-200 bg-amber-50 p-4 flex items-center gap-3">
                          <TrendingDown className="h-8 w-8 text-amber-600 flex-shrink-0" />
                          <div>
                            <p className="text-xs text-slate-600">Despesas</p>
                            <p className="text-lg font-bold text-slate-900">{formatCurrency(reportResumo.total_despesas)}</p>
                          </div>
                        </div>
                        <div className="rounded-lg border border-slate-200 bg-sky-50 p-4 flex items-center gap-3">
                          <DollarSign className="h-8 w-8 text-sky-600 flex-shrink-0" />
                          <div>
                            <p className="text-xs text-slate-600">Saldo</p>
                            <p className={`text-lg font-bold ${(reportResumo.saldo ?? 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                              {formatCurrency(reportResumo.saldo)}
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="rounded-lg border border-slate-200 bg-slate-50 p-6 min-h-[200px]">
                        {reportScope === 'igreja' && reportChartData.length > 0 ? (
                          <ResponsiveContainer width="100%" height={280}>
                            <BarChart data={reportChartData} margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                              <XAxis dataKey="tipo" tick={{ fill: '#64748b', fontSize: 12 }} />
                              <YAxis tick={{ fill: '#64748b', fontSize: 12 }} tickFormatter={(v) => formatCurrency(v)} />
                              <Tooltip formatter={(value) => formatCurrency(value)} contentStyle={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '8px' }} />
                              <Legend />
                              <Bar dataKey="valor" name="Valor" radius={[4, 4, 0, 0]}>
                                {reportChartData.map((entry, index) => (
                                  <Cell key={index} fill={TIPO_CHART_COLORS[entry.tipoKey] || '#94a3b8'} />
                                ))}
                              </Bar>
                            </BarChart>
                          </ResponsiveContainer>
                        ) : reportScope === 'ministerio' && reportChartDataMinisterio.length > 0 ? (
                          <ResponsiveContainer width="100%" height={280}>
                            <BarChart data={reportChartDataMinisterio} margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                              <XAxis dataKey="nome" tick={{ fill: '#64748b', fontSize: 12 }} />
                              <YAxis tick={{ fill: '#64748b', fontSize: 12 }} tickFormatter={(v) => formatCurrency(v)} />
                              <Tooltip formatter={(value) => formatCurrency(value)} contentStyle={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '8px' }} />
                              <Legend />
                              <Bar dataKey="valor" name="Saldo" fill="#0ea5e9" radius={[4, 4, 0, 0]} />
                            </BarChart>
                          </ResponsiveContainer>
                        ) : reportScope === 'igreja' ? (
                          <div className="h-full flex flex-col items-center justify-center text-slate-500">
                            <BarChart2 className="h-10 w-10 text-sky-400 mb-2" />
                            <p className="font-medium text-slate-600">Nenhum lançamento no período</p>
                          </div>
                        ) : reportScope === 'ministerio' ? (
                          <div className="h-full flex flex-col items-center justify-center text-slate-500">
                            <BarChart2 className="h-10 w-10 text-sky-400 mb-2" />
                            <p className="font-medium text-slate-600">Nenhum dado no período</p>
                          </div>
                        ) : (
                          <div className="h-full flex flex-col items-center justify-center text-slate-500">
                            <BarChart2 className="h-10 w-10 text-sky-400 mb-2" />
                            <p className="font-medium text-slate-600">Gráficos</p>
                            <p className="text-sm">Gráficos serão exibidos quando o serviço estiver disponível</p>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )}
                {reportType === 'despesas' && (
                  <Card className="stat-card">
                    <CardHeader>
                      <CardTitle>Relatório de despesas</CardTitle>
                      <CardDescription>
                        {reportScope === 'ministerio' ? 'Todo o ministério' : igrejas.find((i) => i.id === reportIgrejaId)?.nome || 'Igreja'} —{' '}
                        {reportPeriod === 'mes_atual' ? 'Mês atual' : `${reportDataInicio} a ${reportDataFim}`}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      <div className="rounded-lg border border-slate-200 bg-amber-50 p-6 flex items-center gap-4 max-w-sm">
                        <TrendingDown className="h-10 w-10 text-amber-600 flex-shrink-0" />
                        <div>
                          <p className="text-sm text-slate-600">Total de despesas</p>
                          <p className="text-2xl font-bold text-slate-900">{formatCurrency(reportResumo.total_despesas)}</p>
                        </div>
                      </div>
                      <div className="rounded-lg border border-slate-200 bg-slate-50 p-6 min-h-[200px]">
                        {reportScope === 'igreja' && reportChartData.length > 0 ? (
                          <ResponsiveContainer width="100%" height={280}>
                            <BarChart data={reportChartData} margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                              <XAxis dataKey="categoria" tick={{ fill: '#64748b', fontSize: 12 }} />
                              <YAxis tick={{ fill: '#64748b', fontSize: 12 }} tickFormatter={(v) => formatCurrency(v)} />
                              <Tooltip formatter={(value) => formatCurrency(value)} contentStyle={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '8px' }} />
                              <Legend />
                              <Bar dataKey="valor" name="Despesas" radius={[4, 4, 0, 0]}>
                                {reportChartData.map((entry, index) => (
                                  <Cell key={index} fill={COLORS[index % COLORS.length]} />
                                ))}
                              </Bar>
                            </BarChart>
                          </ResponsiveContainer>
                        ) : reportScope === 'ministerio' && reportChartDataMinisterio.length > 0 ? (
                          <ResponsiveContainer width="100%" height={280}>
                            <BarChart data={reportChartDataMinisterio} margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                              <XAxis dataKey="nome" tick={{ fill: '#64748b', fontSize: 12 }} />
                              <YAxis tick={{ fill: '#64748b', fontSize: 12 }} tickFormatter={(v) => formatCurrency(v)} />
                              <Tooltip formatter={(value) => formatCurrency(value)} contentStyle={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '8px' }} />
                              <Legend />
                              <Bar dataKey="valor" name="Despesas" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                            </BarChart>
                          </ResponsiveContainer>
                        ) : reportScope === 'igreja' ? (
                          <div className="h-full min-h-[200px] flex flex-col items-center justify-center text-slate-500">
                            <PieChart className="h-10 w-10 text-amber-400 mb-2" />
                            <p className="font-medium text-slate-600">Nenhum lançamento no período</p>
                          </div>
                        ) : reportScope === 'ministerio' ? (
                          <div className="h-full min-h-[200px] flex flex-col items-center justify-center text-slate-500">
                            <PieChart className="h-10 w-10 text-amber-400 mb-2" />
                            <p className="font-medium text-slate-600">Nenhum dado no período</p>
                          </div>
                        ) : (
                          <div className="h-full flex flex-col items-center justify-center text-slate-500">
                            <PieChart className="h-10 w-10 text-amber-400 mb-2" />
                            <p className="font-medium text-slate-600">Gráficos de despesas</p>
                            <p className="text-sm">Gráficos serão exibidos quando o serviço estiver disponível</p>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )}
                {reportType === 'entradas' && (
                  <Card className="stat-card">
                    <CardHeader>
                      <CardTitle>Relatório de entradas</CardTitle>
                      <CardDescription>
                        {reportScope === 'ministerio' ? 'Todo o ministério' : igrejas.find((i) => i.id === reportIgrejaId)?.nome || 'Igreja'} —{' '}
                        {reportPeriod === 'mes_atual' ? 'Mês atual' : `${reportDataInicio} a ${reportDataFim}`}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      <div className="rounded-lg border border-slate-200 bg-emerald-50 p-6 flex items-center gap-4 max-w-sm">
                        <TrendingUp className="h-10 w-10 text-emerald-600 flex-shrink-0" />
                        <div>
                          <p className="text-sm text-slate-600">Total de entradas (dízimos + ofertas)</p>
                          <p className="text-2xl font-bold text-slate-900">
                            {formatCurrency((reportResumo.total_dizimos || 0) + (reportResumo.total_ofertas || 0))}
                          </p>
                        </div>
                      </div>
                      <div className="rounded-lg border border-slate-200 bg-slate-50 p-6 min-h-[200px]">
                        {reportScope === 'igreja' && reportChartData.length > 0 ? (
                          <ResponsiveContainer width="100%" height={280}>
                            <BarChart data={reportChartData} margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                              <XAxis dataKey="tipo" tick={{ fill: '#64748b', fontSize: 12 }} />
                              <YAxis tick={{ fill: '#64748b', fontSize: 12 }} tickFormatter={(v) => formatCurrency(v)} />
                              <Tooltip formatter={(value) => formatCurrency(value)} contentStyle={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '8px' }} />
                              <Legend />
                              <Bar dataKey="valor" name="Valor" radius={[4, 4, 0, 0]}>
                                {reportChartData.map((entry, index) => (
                                  <Cell key={index} fill={TIPO_CHART_COLORS[entry.tipoKey] || '#94a3b8'} />
                                ))}
                              </Bar>
                            </BarChart>
                          </ResponsiveContainer>
                        ) : reportScope === 'ministerio' && reportChartDataMinisterio.length > 0 ? (
                          <ResponsiveContainer width="100%" height={280}>
                            <BarChart data={reportChartDataMinisterio} margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                              <XAxis dataKey="nome" tick={{ fill: '#64748b', fontSize: 12 }} />
                              <YAxis tick={{ fill: '#64748b', fontSize: 12 }} tickFormatter={(v) => formatCurrency(v)} />
                              <Tooltip formatter={(value) => formatCurrency(value)} contentStyle={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '8px' }} />
                              <Legend />
                              <Bar dataKey="valor" name="Entradas" fill="#10b981" radius={[4, 4, 0, 0]} />
                            </BarChart>
                          </ResponsiveContainer>
                        ) : reportScope === 'igreja' ? (
                          <div className="h-full flex flex-col items-center justify-center text-slate-500">
                            <BarChart2 className="h-10 w-10 text-emerald-400 mb-2" />
                            <p className="font-medium text-slate-600">Nenhum lançamento no período</p>
                          </div>
                        ) : reportScope === 'ministerio' ? (
                          <div className="h-full flex flex-col items-center justify-center text-slate-500">
                            <BarChart2 className="h-10 w-10 text-emerald-400 mb-2" />
                            <p className="font-medium text-slate-600">Nenhum dado no período</p>
                          </div>
                        ) : (
                          <div className="h-full flex flex-col items-center justify-center text-slate-500">
                            <BarChart2 className="h-10 w-10 text-emerald-400 mb-2" />
                            <p className="font-medium text-slate-600">Gráficos de entradas</p>
                            <p className="text-sm">Gráficos serão exibidos quando o serviço estiver disponível</p>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )}
                {reportType === 'dizimo' && (
                  <Card className="stat-card">
                    <CardHeader>
                      <CardTitle>Relatório de dízimo</CardTitle>
                      <CardDescription>
                        {reportScope === 'ministerio' ? 'Todo o ministério' : igrejas.find((i) => i.id === reportIgrejaId)?.nome || 'Igreja'} —{' '}
                        {reportPeriod === 'mes_atual' ? 'Mês atual' : `${reportDataInicio} a ${reportDataFim}`}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      <div className="rounded-lg border border-slate-200 bg-sky-50 p-6 flex items-center gap-4 max-w-sm">
                        <DollarSign className="h-10 w-10 text-sky-600 flex-shrink-0" />
                        <div>
                          <p className="text-sm text-slate-600">Total de dízimos</p>
                          <p className="text-2xl font-bold text-slate-900">{formatCurrency(reportResumo.total_dizimos)}</p>
                        </div>
                      </div>
                      <div className="rounded-lg border border-slate-200 bg-slate-50 p-6 min-h-[200px]">
                        {reportScope === 'igreja' ? (
                          <div className="h-full min-h-[200px] flex flex-col items-center justify-center text-slate-500">
                            <BarChart2 className="h-10 w-10 text-sky-400 mb-2" />
                            <p className="font-medium text-slate-600">Gráfico não exibido para relatório por tipo único</p>
                          </div>
                        ) : reportScope === 'ministerio' && reportChartDataMinisterio.length > 0 ? (
                          <ResponsiveContainer width="100%" height={280}>
                            <BarChart data={reportChartDataMinisterio} margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                              <XAxis dataKey="nome" tick={{ fill: '#64748b', fontSize: 12 }} />
                              <YAxis tick={{ fill: '#64748b', fontSize: 12 }} tickFormatter={(v) => formatCurrency(v)} />
                              <Tooltip formatter={(value) => formatCurrency(value)} contentStyle={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '8px' }} />
                              <Legend />
                              <Bar dataKey="valor" name={TIPO_LABELS.dizimo} fill="#0ea5e9" radius={[4, 4, 0, 0]} />
                            </BarChart>
                          </ResponsiveContainer>
                        ) : reportScope === 'ministerio' ? (
                          <div className="h-full min-h-[200px] flex flex-col items-center justify-center text-slate-500">
                            <BarChart2 className="h-10 w-10 text-sky-400 mb-2" />
                            <p className="font-medium text-slate-600">Nenhum dado no período</p>
                          </div>
                        ) : (
                          <div className="h-full flex flex-col items-center justify-center text-slate-500">
                            <BarChart2 className="h-10 w-10 text-sky-400 mb-2" />
                            <p className="font-medium text-slate-600">Gráficos</p>
                            <p className="text-sm">Gráficos serão exibidos quando o serviço estiver disponível</p>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )}
                {reportType === 'oferta_geral' && (
                  <Card className="stat-card">
                    <CardHeader>
                      <CardTitle>Relatório de oferta geral</CardTitle>
                      <CardDescription>
                        {reportScope === 'ministerio' ? 'Todo o ministério' : igrejas.find((i) => i.id === reportIgrejaId)?.nome || 'Igreja'} —{' '}
                        {reportPeriod === 'mes_atual' ? 'Mês atual' : `${reportDataInicio} a ${reportDataFim}`}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      <div className="rounded-lg border border-slate-200 bg-emerald-50 p-6 flex items-center gap-4 max-w-sm">
                        <TrendingUp className="h-10 w-10 text-emerald-600 flex-shrink-0" />
                        <div>
                          <p className="text-sm text-slate-600">Total de oferta geral</p>
                          <p className="text-2xl font-bold text-slate-900">{formatCurrency(reportResumo.total_ofertas)}</p>
                        </div>
                      </div>
                      <div className="rounded-lg border border-slate-200 bg-slate-50 p-6 min-h-[200px]">
                        {reportScope === 'igreja' ? (
                          <div className="h-full min-h-[200px] flex flex-col items-center justify-center text-slate-500">
                            <BarChart2 className="h-10 w-10 text-emerald-400 mb-2" />
                            <p className="font-medium text-slate-600">Gráfico não exibido para relatório por tipo único</p>
                          </div>
                        ) : reportScope === 'ministerio' && reportChartDataMinisterio.length > 0 ? (
                          <ResponsiveContainer width="100%" height={280}>
                            <BarChart data={reportChartDataMinisterio} margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                              <XAxis dataKey="nome" tick={{ fill: '#64748b', fontSize: 12 }} />
                              <YAxis tick={{ fill: '#64748b', fontSize: 12 }} tickFormatter={(v) => formatCurrency(v)} />
                              <Tooltip formatter={(value) => formatCurrency(value)} contentStyle={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '8px' }} />
                              <Legend />
                              <Bar dataKey="valor" name={TIPO_LABELS.oferta_geral} fill="#10b981" radius={[4, 4, 0, 0]} />
                            </BarChart>
                          </ResponsiveContainer>
                        ) : reportScope === 'ministerio' ? (
                          <div className="h-full min-h-[200px] flex flex-col items-center justify-center text-slate-500">
                            <BarChart2 className="h-10 w-10 text-emerald-400 mb-2" />
                            <p className="font-medium text-slate-600">Nenhum dado no período</p>
                          </div>
                        ) : (
                          <div className="h-full flex flex-col items-center justify-center text-slate-500">
                            <BarChart2 className="h-10 w-10 text-emerald-400 mb-2" />
                            <p className="font-medium text-slate-600">Gráficos</p>
                            <p className="text-sm">Gráficos serão exibidos quando o serviço estiver disponível</p>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )}
                {reportType === 'oferta_especifica' && (
                  <Card className="stat-card">
                    <CardHeader>
                      <CardTitle>Relatório de oferta específica</CardTitle>
                      <CardDescription>
                        {reportScope === 'ministerio' ? 'Todo o ministério' : igrejas.find((i) => i.id === reportIgrejaId)?.nome || 'Igreja'} —{' '}
                        {reportPeriod === 'mes_atual' ? 'Mês atual' : `${reportDataInicio} a ${reportDataFim}`}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      <div className="rounded-lg border border-slate-200 bg-violet-50 p-6 flex items-center gap-4 max-w-sm">
                        <TrendingUp className="h-10 w-10 text-violet-600 flex-shrink-0" />
                        <div>
                          <p className="text-sm text-slate-600">Total de oferta específica</p>
                          <p className="text-2xl font-bold text-slate-900">{formatCurrency(reportResumo.total_ofertas)}</p>
                        </div>
                      </div>
                      <div className="rounded-lg border border-slate-200 bg-slate-50 p-6 min-h-[200px]">
                        {reportScope === 'igreja' ? (
                          <div className="h-full min-h-[200px] flex flex-col items-center justify-center text-slate-500">
                            <BarChart2 className="h-10 w-10 text-violet-400 mb-2" />
                            <p className="font-medium text-slate-600">Gráfico não exibido para relatório por tipo único</p>
                          </div>
                        ) : reportScope === 'ministerio' && reportChartDataMinisterio.length > 0 ? (
                          <ResponsiveContainer width="100%" height={280}>
                            <BarChart data={reportChartDataMinisterio} margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                              <XAxis dataKey="nome" tick={{ fill: '#64748b', fontSize: 12 }} />
                              <YAxis tick={{ fill: '#64748b', fontSize: 12 }} tickFormatter={(v) => formatCurrency(v)} />
                              <Tooltip formatter={(value) => formatCurrency(value)} contentStyle={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '8px' }} />
                              <Legend />
                              <Bar dataKey="valor" name={TIPO_LABELS.oferta_especifica} fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                            </BarChart>
                          </ResponsiveContainer>
                        ) : reportScope === 'ministerio' ? (
                          <div className="h-full min-h-[200px] flex flex-col items-center justify-center text-slate-500">
                            <BarChart2 className="h-10 w-10 text-violet-400 mb-2" />
                            <p className="font-medium text-slate-600">Nenhum dado no período</p>
                          </div>
                        ) : (
                          <div className="h-full flex flex-col items-center justify-center text-slate-500">
                            <BarChart2 className="h-10 w-10 text-violet-400 mb-2" />
                            <p className="font-medium text-slate-600">Gráficos</p>
                            <p className="text-sm">Gráficos serão exibidos quando o serviço estiver disponível</p>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Lista detalhada: por igreja (ministério) ou por tipo (Sede/Congregação) */}
                {reportDetailByIgreja.length > 0 && (
                  <Card className="stat-card">
                    <CardHeader>
                      <CardTitle className="card-title flex items-center gap-2">
                        <FileText className="h-5 w-5 text-sky-600" />
                        {reportScope === 'ministerio' ? 'Detalhamento por igreja' : 'Detalhamento por tipo'}
                      </CardTitle>
                      <CardDescription>
                        {reportScope === 'ministerio'
                          ? 'Resumo por tipo conforme o relatório selecionado'
                          : 'Resumo por tipo/categoria da igreja selecionada'}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-8">
                      {reportScope === 'igreja' ? (
                        <div className="rounded-lg border border-slate-200 overflow-hidden">
                          <Table>
                            <TableHeader>
                              <TableRow className="bg-slate-50">
                                <TableHead className="text-slate-600">Tipo / Categoria</TableHead>
                                <TableHead className="text-right text-slate-600">Valor</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {reportDetailByIgreja[0].linhas.map((linha, idx) => (
                                <TableRow key={idx} className="hover:bg-slate-50/50">
                                  <TableCell className="font-medium text-slate-700">{linha.label}</TableCell>
                                  <TableCell className="text-right font-mono">
                                    {formatCurrency(linha.valor)}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      ) : (
                        reportDetailByIgreja.map((item) => (
                          <div key={item.nome} className="space-y-3">
                            <h3 className="text-lg font-semibold text-slate-800 border-b border-slate-200 pb-2 flex items-center gap-2">
                              <Building2 className="h-5 w-5 text-sky-600" />
                              {item.nome}
                            </h3>
                            <div className="rounded-lg border border-slate-200 overflow-hidden">
                              <Table>
                                <TableHeader>
                                  <TableRow className="bg-slate-50">
                                    <TableHead className="text-slate-600">Tipo / Categoria</TableHead>
                                    <TableHead className="text-right text-slate-600">Valor</TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {item.linhas.map((linha, idx) => (
                                    <TableRow key={idx} className="hover:bg-slate-50/50">
                                      <TableCell className="font-medium text-slate-700">{linha.label}</TableCell>
                                      <TableCell className="text-right font-mono">
                                        {formatCurrency(linha.valor)}
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </div>
                          </div>
                        ))
                      )}
                    </CardContent>
                  </Card>
                )}
                </div>

                <div className="flex justify-end pt-4">
                  <Button
                    type="button"
                    className="gap-2"
                    onClick={handleDownloadReportPdf}
                    disabled={reportPdfLoading}
                  >
                    {reportPdfLoading ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Gerando PDF...
                      </>
                    ) : (
                      <>
                        <Download className="h-4 w-4" />
                        Baixar relatório
                      </>
                    )}
                  </Button>
                </div>
              </>
            )}
            {reportResumo === null && !reportResumoLoading && (
              <Card className="stat-card">
                <CardContent className="py-12 flex flex-col items-center justify-center text-slate-500">
                  <FileBarChart className="h-12 w-12 text-slate-300 mb-4" />
                  <p className="font-medium text-slate-600">Nenhum relatório gerado</p>
                  <p className="text-sm">Selecione os filtros e clique em &quot;Gerar relatório&quot;</p>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* Lançamentos Tab */}
        <TabsContent value="lancamentos">
          <Card className="stat-card overflow-hidden" data-testid="lancamentos-table">
            <CardContent className="p-0">
              {lancamentos.length === 0 ? (
                <div className="text-center py-12">
                  <DollarSign className="mx-auto h-12 w-12 text-slate-300" />
                  <h3 className="mt-4 text-lg font-medium text-slate-900">Nenhum lançamento</h3>
                  <p className="mt-2 text-slate-500">Comece registrando um novo lançamento.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="table-header">
                        <TableHead>Data</TableHead>
                        <TableHead>Tipo</TableHead>
                        <TableHead>Descrição</TableHead>
                        <TableHead className="hidden md:table-cell">Categoria</TableHead>
                        <TableHead className="text-right">Valor</TableHead>
                        <TableHead className="w-12 text-center">Comprovante</TableHead>
                        <TableHead className="text-right">Ações</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {lancamentos.slice(0, 50).map((lancamento) => {
                        const config = getTipoConfig(lancamento.tipo);
                        const isEntrada = lancamento.tipo !== 'despesa' && lancamento.tipo !== 'despesas';
                        return (
                          <TableRow key={lancamento.id} className="hover:bg-slate-50">
                            <TableCell className="text-slate-600">
                              {lancamento.data ? format(new Date(lancamento.data + 'T00:00:00'), 'dd/MM/yyyy') : '-'}
                            </TableCell>
                            <TableCell>
                              <Badge variant="secondary" className={`bg-${config.color}-50 text-${config.color}-700`}>
                                {config.label}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-slate-900">{lancamento.descricao || '-'}</TableCell>
                            <TableCell className="hidden md:table-cell text-slate-600">
                              {lancamento.categoria || '-'}
                            </TableCell>
                            <TableCell className={`text-right font-semibold ${isEntrada ? 'text-emerald-600' : 'text-amber-600'}`}>
                              {isEntrada ? '+' : '-'} {formatCurrency(lancamento.valor)}
                            </TableCell>
                            <TableCell className="text-center">
                              {lancamento.comprovante_url ? (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 text-sky-600 hover:text-sky-700 hover:bg-sky-50"
                                  onClick={() => setComprovanteModal({ open: true, url: lancamento.comprovante_url })}
                                  title="Ver comprovante"
                                  data-testid={`comprovante-${lancamento.id}`}
                                >
                                  <Paperclip className="h-4 w-4" />
                                </Button>
                              ) : (
                                <span className="text-slate-300">-</span>
                              )}
                            </TableCell>
                            <TableCell className="text-right">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => setDeleteDialog({ open: true, lancamento })}
                                className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Gráficos Tab */}
        <TabsContent value="graficos">
          <Card className="stat-card">
            <CardHeader>
              <CardTitle className="card-title">Movimento Anual</CardTitle>
              <CardDescription>Comparativo de entradas e saídas ao longo do ano</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-96">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={graficoData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="nome_mes" tick={{ fill: '#64748b', fontSize: 12 }} />
                    <YAxis tick={{ fill: '#64748b', fontSize: 12 }} tickFormatter={(value) => `R$${value}`} />
                    <Tooltip
                      formatter={(value) => formatCurrency(value)}
                      contentStyle={{
                        backgroundColor: 'white',
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                      }}
                    />
                    <Legend />
                    <Bar dataKey="entradas" name="Entradas" fill="#0ea5e9" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="saidas" name="Saídas" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Modal Comprovante */}
      <Dialog
        open={comprovanteModal.open}
        onOpenChange={(open) => !open && setComprovanteModal({ open: false, url: null })}
      >
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Paperclip className="h-5 w-5 text-sky-600" />
              Comprovante
            </DialogTitle>
          </DialogHeader>
          {comprovanteModal.url && (
            <div className="flex flex-col gap-4 flex-1 min-h-0">
              {/\.(pdf|PDF)$/.test(comprovanteModal.url) || comprovanteModal.url.toLowerCase().includes('pdf') ? (
                <div className="flex flex-1 flex-col items-center justify-center rounded-lg border border-slate-200 bg-slate-50 p-8 min-h-[280px]">
                  <FileText className="h-16 w-16 text-slate-400 mb-4" />
                  <p className="text-sm text-slate-600 mb-4">Documento PDF</p>
                  <Button
                    asChild
                    className="btn-primary"
                  >
                    <a href={comprovanteModal.url} target="_blank" rel="noopener noreferrer" download>
                      <Download className="mr-2 h-4 w-4" />
                      Baixar comprovante
                    </a>
                  </Button>
                </div>
              ) : (
                <>
                  <div className="flex-1 min-h-0 rounded-lg border border-slate-200 bg-slate-100 overflow-auto flex items-center justify-center p-2">
                    <img
                      src={comprovanteModal.url}
                      alt="Comprovante"
                      className="max-w-full max-h-[60vh] object-contain rounded"
                    />
                  </div>
                  <Button asChild variant="outline" className="w-full sm:w-auto">
                    <a href={comprovanteModal.url} target="_blank" rel="noopener noreferrer" download>
                      <Download className="mr-2 h-4 w-4" />
                      Baixar comprovante
                    </a>
                  </Button>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, lancamento: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir Lançamento</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir este lançamento de {formatCurrency(deleteDialog.lancamento?.valor)}?
              Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Financeiro;
